import { iframeDataBridge } from '@/iframe/iframeDataBridge.js';
import { authenticatedFetch, getApiUrl } from '@/utils/authService.js';
import { getAttributes } from '@/api/core/attribute.js';
import { log } from '@/utils/vueLogger.js';
import { CacheManager } from '@/utils/cacheManager.js';
import { registerQueueHandler, enqueueOperation } from '@/sync/offlineSyncQueue.js';
import { useTranslator } from '@/utils/useTranslator';

const deliveryLocationAttributeCache = new CacheManager('VueCache', 'cache', {
	maxAge: 24 * 60 * 60 * 1000,
	logCategory: 'DeliveryLocationAttribute',
	keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.deliveryLocationAttributes.`,
});

function getAttributeId(attribute) {
	const attributeId = Number(attribute?.id ?? attribute?.attributeId);
	return Number.isInteger(attributeId) && attributeId > 0 ? attributeId : null;
}

async function filterKnownDeliveryLocationAttributes(items, tokenType = 'tokenSales') {
	if (!Array.isArray(items)) {
		return [];
	}

	try {
		const attributes = await getAttributes(false, tokenType);
		if (!Array.isArray(attributes) || attributes.length === 0) {
			return items;
		}

		const validAttributeIds = new Set(attributes.map((attribute) => Number(attribute?.id)).filter((attributeId) => Number.isInteger(attributeId) && attributeId > 0));
		return items.map((item) => ({
			...item,
			attributes: Array.isArray(item?.attributes)
				? item.attributes.filter((attribute) => {
						const attributeId = getAttributeId(attribute);
						return attributeId !== null && validAttributeIds.has(attributeId);
					})
				: [],
		}));
	} catch (error) {
		log('warn', 'DeliveryLocationAttribute', `Unable to filter delivery location attributes from attribute definitions: ${error.message}`);
		return items;
	}
}

function normalizeDeliveryLocationIds(deliveryLocationIds = []) {
	if (!Array.isArray(deliveryLocationIds)) {
		return [];
	}

	return [...new Set(deliveryLocationIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0))].sort((a, b) => a - b);
}

function buildDeliveryLocationAttributesCacheKey(deliveryLocationIds = []) {
	const normalizedIds = normalizeDeliveryLocationIds(deliveryLocationIds);
	return normalizedIds.length ? `delivery-locations:${normalizedIds.join(',')}` : 'delivery-locations:none';
}

function buildDeliveryLocationAttributesUrl(deliveryLocationIds = []) {
	const normalizedIds = normalizeDeliveryLocationIds(deliveryLocationIds);
	const apiUrl = getApiUrl('Sales/DeliveryLocationAttributes');

	if (!normalizedIds.length) {
		return apiUrl;
	}

	const query = new URLSearchParams();
	normalizedIds.forEach((id) => query.append('deliveryLocationIds', String(id)));
	return `${apiUrl}?${query.toString()}`;
}

export async function getDeliveryLocationAttributes(deliveryLocationIds = [], forceRefresh = false, tokenType = 'tokenSales') {
	const cacheKey = buildDeliveryLocationAttributesCacheKey(deliveryLocationIds);

	try {
		if (!forceRefresh) {
			const cached = await deliveryLocationAttributeCache.get(cacheKey);
			if (cached) {
				log('info', 'DeliveryLocationAttribute', `Returning cached delivery location attributes (${cacheKey})`);
				return await filterKnownDeliveryLocationAttributes(cached, tokenType);
			}
		}

		if (iframeDataBridge.isOffline() && !forceRefresh) {
			const normalizedIds = normalizeDeliveryLocationIds(deliveryLocationIds);
			if (normalizedIds.length) {
				const allCached = await deliveryLocationAttributeCache.get(buildDeliveryLocationAttributesCacheKey([]));
				if (allCached) {
					log('info', 'DeliveryLocationAttribute', 'Offline: Filtering from cached all delivery location attributes');
					const filtered = allCached.filter((item) => normalizedIds.includes(Number(item?.deliveryLocationId ?? item?.id)));
					return await filterKnownDeliveryLocationAttributes(filtered, tokenType);
				}
			}
			log('warn', 'DeliveryLocationAttribute', 'Offline: No cached delivery location attributes available');
			return [];
		}

		const response = await authenticatedFetch(buildDeliveryLocationAttributesUrl(deliveryLocationIds), { method: 'GET' }, tokenType);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const data = await filterKnownDeliveryLocationAttributes(await response.json(), tokenType);
		await deliveryLocationAttributeCache.put(cacheKey, data);

		log('info', 'DeliveryLocationAttribute', `Fetched ${data?.length || 0} delivery location attributes`);
		return data;
	} catch (error) {
		log('error', 'DeliveryLocationAttribute', `Error loading delivery location attributes: ${error.message}`);

		const cached = await deliveryLocationAttributeCache.get(cacheKey);
		if (cached) {
			log('info', 'DeliveryLocationAttribute', 'Returning cached delivery location attributes after error');
			return await filterKnownDeliveryLocationAttributes(cached, tokenType);
		}

		throw error;
	}
}

registerQueueHandler('postDeliveryLocationAttributes', async ({ deliveryLocationAttributes, tokenType, deliveryLocationIds }) => {
	const success = await postDeliveryLocationAttributes(deliveryLocationAttributes, tokenType, deliveryLocationIds);
	if (!success) throw new Error('Failed to post delivery location attributes');
});

export async function postDeliveryLocationAttributes(deliveryLocationAttributes = [], tokenType = 'tokenSales', deliveryLocationIds = []) {
	const t = useTranslator();
	const toast = iframeDataBridge.getIframeWindow()?.toast;

	if (!iframeDataBridge.isOnline()) {
		const cacheKey = buildDeliveryLocationAttributesCacheKey(deliveryLocationIds);
		await deliveryLocationAttributeCache.put(cacheKey, JSON.parse(JSON.stringify(deliveryLocationAttributes)));
		log('info', 'DeliveryLocationAttribute', '💾 Offline: Optimistic cache update applied');

		await enqueueOperation('postDeliveryLocationAttributes', { deliveryLocationAttributes, tokenType, deliveryLocationIds }, 'Delivery location attributes');
		toast?.(t('PendingSave'), 'success', true);
		return true;
	}

	try {
		const apiUrl = getApiUrl('Sales/DeliveryLocationAttributes');
		const response = await authenticatedFetch(
			apiUrl,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(deliveryLocationAttributes),
			},
			tokenType
		);

		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		await clearDeliveryLocationAttributeCache();
		log('success', 'DeliveryLocationAttribute', `Posted ${deliveryLocationAttributes?.length || 0} delivery location attributes successfully`);
		return true;
	} catch (error) {
		log('error', 'DeliveryLocationAttribute', `Failed to post delivery location attributes: ${error.message}`);
		return false;
	}
}

export async function clearDeliveryLocationAttributeCache(key = null) {
	if (key) {
		await deliveryLocationAttributeCache.delete(key);
	} else {
		await deliveryLocationAttributeCache.clearAll();
	}
}

export async function cleanOldDeliveryLocationAttributeCache(maxAgeInDays = 1) {
	await deliveryLocationAttributeCache.cleanOld(maxAgeInDays);
}

export async function getDeliveryLocationAttributeCacheStats() {
	return await deliveryLocationAttributeCache.getStats();
}

cleanOldDeliveryLocationAttributeCache();
